import React, { useLayoutEffect, useRef } from 'react';
import { ScrollSmoother, ScrollTrigger, prefersReducedMotion } from './landing/gsap';
import './ScrollSmoother.css';

// Désactivé au chargement du module (avant le premier rendu) : le navigateur
// ne doit pas restaurer l'ancienne position de scroll sur les pages animées.
if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

/** Dernière route pour laquelle le scroll a été remis en haut */
let lastResetPath: string | null = null;

interface ScrollSmootherProps {
  children?: React.ReactNode;
  smooth?: number;
  effects?: boolean;
  normalizeScroll?: boolean;
}

/**
 * Wrapper ScrollSmoother.
 * Créé dans un useLayoutEffect pour exister AVANT les ScrollTriggers des enfants
 * (qui tournent dans des useEffect), ce qui garantit un pinType correct.
 * Les éléments en position: fixed (header) doivent rester hors de ce wrapper.
 */
export default function ScrollSmootherComponent({
  children,
  smooth = 1.2,
  effects = true,
  normalizeScroll = true,
}: ScrollSmootherProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const smootherRef = useRef<any | null>(null);

  useLayoutEffect(() => {
    if (!wrapperRef.current || !contentRef.current) return;

    const reduced = prefersReducedMotion();

    // Chaque page publique monte son propre smoother : on repart du haut
    // à chaque changement de route (pas lors d'un simple remontage, ex. HMR).
    const path = window.location.pathname;
    const resetScroll = lastResetPath !== path;
    lastResetPath = path;
    if (resetScroll) window.scrollTo(0, 0);

    try {
      // @ts-ignore
      smootherRef.current = ScrollSmoother.create({
        wrapper: wrapperRef.current,
        content: contentRef.current,
        smooth: reduced ? 0 : smooth,
        effects: !reduced && effects,
        normalizeScroll,
        ignoreMobileResize: true,
      });
      if (resetScroll) smootherRef.current?.scrollTop(0);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('ScrollSmoother unavailable', e);
    }

    const refresh = () => {
      try {
        // @ts-ignore
        ScrollTrigger.refresh();
      } catch (err) {
        /* noop */
      }
    };

    const timers = [
      window.setTimeout(refresh, 120),
      window.setTimeout(refresh, 600),
    ];
    if (document.fonts?.ready) {
      document.fonts.ready.then(refresh).catch(() => undefined);
    }
    window.addEventListener('load', refresh);
    window.addEventListener('resize', refresh);

    return () => {
      timers.forEach((id) => window.clearTimeout(id));
      window.removeEventListener('load', refresh);
      window.removeEventListener('resize', refresh);
      if (smootherRef.current) {
        smootherRef.current.kill();
        smootherRef.current = null;
      }
    };
  }, [smooth, effects, normalizeScroll]);

  return (
    <div id="smooth-wrapper" ref={wrapperRef}>
      <div id="smooth-content" ref={contentRef}>
        {children}
      </div>
    </div>
  );
}
